namespace st.search {
    class FacetService implements services.IFacetService {

        public facetGroups: IFacetGroups = {};
        public isSearching: { [group: string]: boolean } = {};
        public results: { [group: string]: services.ISearchResponse } = {};

        private _firstLoad = true;
        private _stSearchConfig: configuration.ISearchConfig;
        private _stSearchGroupConfig: configuration.ISearchGroupConfigs;
        private _registeredGroups: string[] = [];
        private _groupsUpdated: string[] = null;
        private _constantFilters: { [group: string]: { [key: string]: string[] } } = {};

        // private static NameRegex: RegExp = /^(.+)\[(\d+)\]$/;

        static $inject = ['$injector', '$rootScope', '$location', '$http', '$q', '$log'];

        constructor(
                $injector: angular.auto.IInjectorService,
                private $rootScope: angular.IRootScopeService,
                private $location: angular.ILocationService,
                private $http: angular.IHttpService,
                private $q: angular.IQService,
                private $log: angular.ILogService) {

            if ($injector.has('stSearchConfig') && $injector.has('stSearchGroupConfig')) {
                this._stSearchConfig = <configuration.ISearchConfig>$injector.get('stSearchConfig');
                this._stSearchGroupConfig = <configuration.ISearchGroupConfigs>$injector.get('stSearchGroupConfig');
            } else {
                $log.error('Search Configuration was never declared. You must place a Facet Search Results widget on the page.');
            }

            $rootScope.$on('$locationChangeSuccess', (e: angular.IAngularEvent, newVal: string, oldVal: string) => {
                if (this._firstLoad) {
                    $rootScope.$emit('stRegisterFacets');
                    let args = <any[]>Object.keys(this.facetGroups);
                    args.unshift(this._registeredGroups);
                    addUnique.apply(null, args);
                }

                if (!this._groupsUpdated) {
                    let search = angular.copy($location.search());
                    // Ignore Page
                    if (search.page) {
                        delete search.page;
                    }

                    angular.forEach(search, (item: string, key: string) => {
                        if (key !== TermPropertyName) {
                            // TODO: if this allows a tree of facets/tags, then item will be an Array
                            // e.g. name=val1&name=val2&name=val3 => { name: [ 'val1', 'val2', 'val3' ] }
                            search[key] = item.split(UrlSeparator);
                        }
                    });

                    let updatedGroups: string[] = [];

                    $rootScope.$emit('stFacetsInit', search, updatedGroups);

                    updatedGroups.forEach(group => {
                        this.groupUpdated(group);
                    });
                }

                if (this._firstLoad) {
                    this._registeredGroups.forEach(group => {
                        const config = this._stSearchGroupConfig[group];
                        if (config.searchOnLoad) {
                            this.groupUpdated(group);
                        }
                    });
                }

                if (this._groupsUpdated && this._groupsUpdated.length) {
                    $log.debug('perform search', this._groupsUpdated);

                    this._groupsUpdated.forEach(group => {
                        this.submitSearch(group);
                    });

                    this._groupsUpdated = null;
                }
                this._firstLoad = false;
            });
        }

        registerFacets(group: string, name?: string, filters?: models.IFacetItem[] | { term: string }): void {
            group = group || DefaultGroup;
            let facetGroup = this.facetGroups[group] = this.facetGroups[group] || {};
            if (name && filters) {
                if (Array.isArray(filters)) {
                    if (filters.length) {
                        facetGroup[name] = filters;
                    }
                } else {
                    facetGroup[name] = filters;
                }
            }
        }

        setPage(pageId: number, group: string): void {
            group = group || DefaultGroup;
            let currentPageId = this.getPageId(group);
            if (currentPageId !== pageId) {
                // TODO: set group in the URL
                this.$location.search(PagePropertyName, pageId > 1 ? pageId : null);
                this.groupUpdated(group);
            }
        }

        setSortBy(group: string, ...sortBy: string[]): void {
            group = group || DefaultGroup;
            const currentSortBy = this.getSortBy(group);
            if (!angular.equals(currentSortBy, sortBy)) {
                let qSort: string = null;
                if (sortBy && sortBy.length) {
                    qSort = sortBy.reduce<string[]>((prev: string[], item: string) => {
                        if (item && (item = item.trim())) {
                            prev.push(item);
                        }
                        return prev;
                    }, []).join(SortSeparator);
                }
                // reset page to 1 if need be
                const currentPageId = this.getPageId(group);
                if (currentPageId !== 1) {
                    this.$location.search(PagePropertyName, null);
                }
                // TODO: set group in the URL
                this.$location.search(SortPropertyName, qSort || null);
                this.groupUpdated(group);
            }
        }

        addConstantFilters(group: string, filters: { [key: string]: string[] }): void {
            let filterAdded = false;
            group = group || DefaultGroup;
            let groupFilters = this._constantFilters[group] = this._constantFilters[group] || {};
            angular.forEach(filters, (values, key) => {
                let groupItems = groupFilters[key] = groupFilters[key] || [];
                for (let i = 0; i < values.length; i++) {
                    if (values[i]) {
                        groupItems.push(values[i]);
                        filterAdded = true;
                    }
                }
            });
            if (filterAdded) {
                addUnique(this._registeredGroups, group);
            }
        }

        update(name: string, group: string, doSearch = true): void {
            group = group || DefaultGroup;
            let facetGroup = this.facetGroups[group];
            if (!facetGroup) {
                return;
            }
            let value: string | models.IFacetItem[] = null;
            if (name === TermPropertyName) {
                if (facetGroup.term && facetGroup.term.term) {
                    value = facetGroup.term.term;
                }
            } else {
                value = <models.IFacetItem[]>facetGroup[name];
            }
            this.updateUrl(value, name);

            // reset page to 1 if need be
            let currentPageId = this.getPageId(group);
            if (currentPageId !== 1) {
                this.$location.search(PagePropertyName, null);
            }

            if (doSearch) {
                this.groupUpdated(group);
            }

            this.$rootScope.$emit('stSearchUpdated', group, name, value);
            this.$log.debug('stFacetUpdated', group, name, value);
        }

        private updateUrl(value: string | models.IFacetItem[], name: string): void {
            let qValue: string;
            if (!value) {
                qValue = null;
            } else if (typeof value === 'string') {
                qValue = value;
            } else if (value.length) {
                qValue = value.reduce((items: string[], item: models.IFacetItem) => {
                    if (item.selected) {
                        items.push(item.name);
                    }
                    return items;
                }, []).join(UrlSeparator);
            }

            if (!qValue) {
                qValue = null;
            }
            if (!this.$location.path()) {
                this.$location.path('/');
            }
            this.$location.search(name, qValue);
        }

        private submitSearch(group: string): void {
            let req: services.ISearchRequest = {
                Filters: {},
                Group: group
            };
            if (!this._stSearchConfig || !this._stSearchGroupConfig) {
                return;
            }
            let hasFilters = false;
            let hasConstFilters = false;
            let groupConfig = this._stSearchGroupConfig[group];
            if (!groupConfig.indexName) {
                this.$log.error('Search Index not specified for', group ? ('group: "' + group + '"') : 'default group');
                return;
            }
            req.ItemsPerPage = groupConfig.itemsPerPage;
            req.OrFacets = groupConfig.orFacets;
            req.SearchFields = groupConfig.searchFields;
            req.HighlightedFields = groupConfig.highlightedFields;

            let constFilters = this._constantFilters[group] || {};
            angular.forEach(constFilters, (items, key) => {
                let filters = req.Filters[key] = [];
                if (key && items) {
                    for (let i = 0; i < items.length; i++) {
                        if (items[i]) {
                            filters.push(items[i]);
                            hasConstFilters = true;
                        }
                    }
                }
            });

            let facetGroup = this.facetGroups[group];
            angular.forEach(facetGroup, (value, name) => {
                if (name === TermPropertyName) {
                    let termCtrl = <controllers.ITermFacetController>value;
                    if (termCtrl && termCtrl.term) {
                        req.Term = termCtrl.term;
                    }
                } else {
                    let items = <models.IFacetItem[]>value;
                    items.forEach(facet => {
                        if (facet.selected) {
                            (req.Filters[name] = req.Filters[name] || []).push(facet.id);
                            hasFilters = true;
                        }
                    });
                }
            });
            if (!hasFilters && !hasConstFilters) {
                delete req.Filters;
            }
            if (groupConfig.allowEmptySearch || req.Term || hasFilters) {
                this.isSearching[group] = true;
                let respPromises: angular.IHttpPromise<services.ISearchResponse>[] = [];
                let pageId = this.getPageId(group);
                let sortBy = this.getSortBy(group);

                req.Skip = req.ItemsPerPage * (pageId - 1);
                if (sortBy && sortBy.length) {
                    req.SortBy = sortBy;
                }
                let firstResp = this.submitSearchReq(req, groupConfig.indexName);

                if (pageId > 1 && this._firstLoad && groupConfig.loadAllPagesOnInit) {
                    while (pageId > 1) {
                        pageId--;
                        req = angular.copy(req);
                        req.Skip = req.ItemsPerPage * (pageId - 1);
                        respPromises.push(this.submitSearchReq(req, groupConfig.indexName));
                    }
                }
                respPromises.push(firstResp);

                this.$q.all(respPromises)
                    .then(resps => {
                        let pages = resps.map(resp => {
                            return resp.data;
                        });
                        let arg = pages.length === 1 ? pages[0] : pages;
                        this.results[group] = pages[pages.length - 1];
                        this.$rootScope.$emit('stSearchResponse', arg, group);
                        this.$log.debug('Search result for group:', group, '; Result:', arg);
                    }, data => {
                        this.$log.error('Error submitting search for group:', group, '; Error data:', data);
                    })
                    .finally(() => {
                        this.isSearching[group] = false;
                    });
            } else {
                this.results[group] = null;
                this.$rootScope.$emit('stSearchResponse', null, group);
                this.$log.debug('Search Cleared for group:', group);
            }
        }

        getPageId(group: string): number {
            let qPage: string;
            let search = this.$location.search();
            if (this._registeredGroups.length > 1) {
                qPage = search[(group || DefaultGroup) + UrlSeparator + PagePropertyName];
            } else {
                qPage = search[PagePropertyName];
            }
            if (qPage) {
                let pageId = parseInt(qPage, 10);
                if (pageId > 1) {
                    return pageId;
                }
            }
            return 1;
        }

        getSortBy(group: string): string[] {
            let qSort: string;
            const search = this.$location.search();
            if (this._registeredGroups.length > 1) {
                qSort = search[(group || DefaultGroup) + UrlSeparator + SortPropertyName];
            } else {
                qSort = search[SortPropertyName];
            }
            if (qSort) {
                return qSort.split(SortSeparator).reduce<string[]>((prev: string[], item: string) => {
                    if (item && (item = item.trim())) {
                        prev.push(item);
                    }
                    return prev;
                }, []);
            }
            return [];
        }

        private submitSearchReq(
                request: services.ISearchRequest,
                indexName: string
        ) {
            return this.$http.post<services.ISearchResponse>(
                this._stSearchConfig.endpoint + '/' + indexName, request);
        }

        private groupUpdated(group: string) {
            if (!this._groupsUpdated) {
                this._groupsUpdated = [];
            }
            addUnique(this._groupsUpdated, group || DefaultGroup);
        }
    }

    function addUnique<T>(coll: T[], ...items: T[]) {
        if (items && items.length) {
            items.forEach(item => {
                if (coll.indexOf(item) === -1) {
                    coll.push(item);
                }
            });
        }
    }

    angular.module('st.search')
        .service('stFacetService', FacetService);
}
